import Button from 'components/commons/button/Button';
import InputNumber from 'components/commons/input/InputNumber';
import Loading from 'components/commons/loading/Loading';
import { useManulAsset } from 'hooks/useManualAssets';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { updateManualAsset } from 'config/api';
import { formatDate, formatNumber } from 'utils/formatter';
import {
  ManualAssetType,
  manualAssetType,
  manualAssetTypes,
} from 'components/features/utils/manualAssetType';
import { linkToOptionalAssetRegistration } from './OptionalAssetRegistration';
import { useRouting } from 'hooks/useRouting';
import { useGlobalOneShot } from 'hooks/useGlobalOneShot';

const minAmount = 1;
const maxAmount = 99999999999;

type Props = Record<string, unknown>;

const OptionalAssetRenewal: React.FC<Props> = () => {
  const { id } = useParams<{ id: string }>();
  const [manualAsset, { callGetManualAsset }] = useManulAsset();
  const routing = useRouting();
  const [disabled, setDisabled] = useState(false);
  const oneShot = useGlobalOneShot('optionalAssetRenewal');

  const [hasNotIdState, setHasNotIdState] = useState<{
    selectedID: string;
    assetAmount: {
      valid: boolean;
      value: number;
    };
  }>({
    selectedID: '',
    assetAmount: {
      valid: true,
      value: 0,
    },
  });
  const [hasIdState, setHasIdState] = useState<{
    assetName: string;
    assetAmount: {
      valid: boolean;
      value: number;
    };
    assetType: ManualAssetType;
  }>({
    assetName: '',
    assetAmount: {
      valid: true,
      value: 0,
    },
    assetType: manualAssetType.その他,
  });

  useEffect(
    () => {
      if (!id) return;
      let mounted = true;
      (async () => {
        const data = await callGetManualAsset(id);
        if (mounted) {
          setHasIdState({
            assetName: data?.manualAssets[0].assetName || '',
            assetAmount: {
              valid: true,
              value: data?.manualAssets[0].assetAmount || 0,
            },
            assetType:
              data?.manualAssets[0].assetType || manualAssetType.その他,
          });
        }
      })();
      return () => {
        mounted = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      if (id) return;

      let mounted = true;
      (async () => {
        const data = await callGetManualAsset();
        const defaultData = data?.manualAssets[0] ?? null;
        if (defaultData === null) {
          return;
        }
        if (mounted) {
          setHasNotIdState({
            assetAmount: {
              valid: true,
              value: defaultData.assetAmount,
            },
            selectedID: defaultData.id,
          });
        }
      })();
      return () => {
        mounted = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      if (id) return;

      setHasNotIdState(prev => ({
        ...prev,
        assetAmount: {
          valid: true,
          value:
            manualAsset.state?.manualAssets.find(
              x => x.id === hasNotIdState.selectedID
            )?.assetAmount ?? 0,
        },
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasNotIdState.selectedID]
  );

  if (!manualAsset.loaded || !manualAsset.state) {
    return <Loading style={{ marginTop: '2.5rem' }} loading />;
  }

  const selectedManualAsset =
    manualAsset.state.manualAssets.find(
      x => x.id === hasNotIdState.selectedID
    ) || null;

  function update(
    id: string,
    assetName: string,
    assetType: ManualAssetType,
    assetAmount: {
      valid: boolean;
      value: number;
    }
  ) {
    if (!assetAmount.valid) return;

    oneShot.call(
      async () => {
        await updateManualAsset(id, assetName, assetType, assetAmount.value);
        routing.backRouting(true);
      },
      true,
      () => setDisabled(true)
    );
  }

  function updateHandle() {
    setDisabled(true);
    // 初期状態でidをもっているかでupdateを呼び出す引数を変更
    if (id) {
      update(
        id,
        hasIdState.assetName,
        hasIdState.assetType,
        hasIdState.assetAmount
      );
    } else {
      if (!selectedManualAsset) {
        return;
      }
      update(
        selectedManualAsset.id,
        selectedManualAsset.assetName,
        selectedManualAsset.assetType,
        {
          valid: hasNotIdState.assetAmount.valid,
          value: hasNotIdState.assetAmount.value,
        }
      );
    }
  }

  function cancel() {
    oneShot.call(() => {
      routing.backRouting(true);
    });
  }

  function handleClickOptionalAssetRegistration() {
    oneShot.call(() => {
      const value = linkToOptionalAssetRegistration('renewal');
      routing.replaceRouting(
        {
          path: value.pathname,
          state: value.state,
        },
        true
      );
    });
  }

  function handleClickSecurityAccounts() {
    oneShot.call(() => {
      routing.pushRouting({ path: '/securityaccounts' }, true);
    });
  }

  function customValidate(value: string, valueAsNumber: number) {
    return /^([1-9][0-9]*)$/.test(value);
  }

  const inputs: {
    label: string;
    input: JSX.Element;
    error?: {
      valid: boolean;
      elem: JSX.Element;
    };
  }[] = !id
    ? [
        {
          label: '資産名・口座名',
          input: (
            <div className="form-select-control-col form-fluid">
              <select
                className="appearance-none"
                value={hasNotIdState.selectedID}
                onChange={event => {
                  const id = event.currentTarget.value;
                  setHasNotIdState(prev => {
                    return {
                      ...prev,
                      selectedID: id,
                    };
                  });
                }}
                disabled={id !== undefined}>
                {manualAsset.state.manualAssets.map((x, i) => (
                  <option key={i} value={x.id}>
                    {x.assetName}
                  </option>
                ))}
              </select>
              <div className="icon-chevron-down ml-auto text-lg text-bluegray-400"></div>
            </div>
          ),
        },
        {
          label: `金額（${formatDate(new Date(), 'yyyy年M月d日')}時点）`,
          input: (
            <div className="form-control-col">
              <InputNumber
                className="form-control form-fluid form-number"
                customValidate={value =>
                  customValidate(value.value, value.valueAsNumber)
                }
                inputMode="numeric"
                max={maxAmount}
                min={minAmount}
                onChange={value => {
                  setHasNotIdState(prev => {
                    return {
                      ...prev,
                      assetAmount: value,
                    };
                  });
                }}
                placeholder="0"
                value={hasNotIdState.assetAmount.value}
                disabled={selectedManualAsset === null}
              />
              <div className="unit">円</div>
            </div>
          ),
          error: {
            valid: hasNotIdState.assetAmount.valid,
            elem: (
              <div className="form-error-note" style={{ marginBottom: '10px' }}>
                <div className="icon-alert"></div>
                金額は{minAmount}円以上{formatNumber(maxAmount, {})}
                円以下の整数を入力してください
              </div>
            ),
          },
        },
      ]
    : [
        {
          label: '資産名・口座名',
          input: (
            <div className="form-control-col">
              <input
                type="text"
                placeholder="○○銀行、タンス貯金、不動産など"
                className="form-control form-fluid"
                value={hasIdState.assetName}
                onChange={event => {
                  const name = event.currentTarget.value;
                  setHasIdState(prev => {
                    return {
                      ...prev,
                      assetName: name,
                    };
                  });
                }}
              />
            </div>
          ),
        },
        {
          label: `金額（${formatDate(new Date(), 'yyyy年M月d日')}時点）`,
          input: (
            <div className="form-control-col">
              <InputNumber
                className="form-control form-fluid form-number"
                customValidate={value =>
                  customValidate(value.value, value.valueAsNumber)
                }
                inputMode="numeric"
                max={maxAmount}
                min={minAmount}
                onChange={value =>
                  setHasIdState(prev => {
                    return {
                      ...prev,
                      assetAmount: value,
                    };
                  })
                }
                placeholder="0"
                value={hasIdState.assetAmount.value}
              />
              <div className="unit">円</div>
            </div>
          ),
          error: {
            valid: hasIdState.assetAmount.valid,
            elem: (
              <div className="form-error-note" style={{ marginBottom: '10px' }}>
                <div className="icon-alert"></div>
                金額は{minAmount}円以上{formatNumber(maxAmount, {})}
                円以下の整数を入力してください
              </div>
            ),
          },
        },
        {
          label: '種別',
          input: (
            <div className="form-select-control-col form-fluid">
              <select
                className="appearance-none"
                value={hasIdState.assetType}
                onChange={event => {
                  const type = Number(event.currentTarget.value);
                  setHasIdState(prev => {
                    return {
                      ...prev,
                      assetType: type as ManualAssetType,
                    };
                  });
                }}>
                {manualAssetTypes.map((x, i) => (
                  <option key={i} value={x.id}>
                    {x.title}
                  </option>
                ))}
              </select>
              <div className="icon-chevron-down ml-auto text-lg text-bluegray-400"></div>
            </div>
          ),
        },
      ];

  const buttonDisabled = !id
    ? !hasNotIdState.assetAmount.valid || !selectedManualAsset
    : !hasIdState.assetAmount.valid || !hasIdState.assetName;
  return (
    <>
      <main className="main">
        <div className="section-block">
          <div className="section-title-row">
            <div className="section-secondary-title">資産情報の更新</div>
            {!id && (
              <div
                className="link-primary align-right cursor-pointer"
                onClick={() => {
                  handleClickOptionalAssetRegistration();
                }}>
                <div className="link-icon icon-plus"></div>
                新規資産情報
              </div>
            )}
          </div>
          <div className="banner-primary asset-refresh">
            <img
              src="/static/images/advisor/asset-refresh.svg"
              className="banner-primary-image"
              alt=""
            />
          </div>
          <div className="form-block">
            {inputs.map((input, i) => {
              return (
                <React.Fragment key={i}>
                  <div className="vertical-form-col">
                    <div className="vertical-form-label">{input.label}</div>
                    {input.input}
                  </div>
                  {input.error && !input.error.valid && input.error.elem}
                </React.Fragment>
              );
            })}
            <div className="section-third-note">
              ※口座連携していない資産を入力してください。重複登録にご注意ください。
            </div>
            <div className="link-row">
              {!id && (
                <div
                  className="link-primary align-right cursor-pointer"
                  onClick={() => handleClickSecurityAccounts()}>
                  お持ちの口座を連携・確認する
                </div>
              )}
            </div>
          </div>
          <div className="btn-vertical-col">
            <Button
              disabled={disabled || buttonDisabled}
              fluid
              onClick={() => updateHandle()}
              primary>
              登録
            </Button>
            <Button fluid onClick={() => cancel()} text>
              キャンセル
            </Button>
          </div>
        </div>
      </main>
    </>
  );
};

export default OptionalAssetRenewal;
